import { useState, useCallback } from 'react';
import { Button } from 'src/components/ui/button';
import { Textarea } from 'src/components/ui/textarea';
import { SparkleIcon, ArrowRightIcon } from '@phosphor-icons/react';
import { T, useGT } from 'gt-react';
import { cn } from 'src/lib/utils';
import { useAssistStepConfig } from 'src/api/rpc/hooks/workflows/useAssistStepConfig';

interface StepConfigAssistantProps {
  stepType: string;
  config: Record<string, unknown>;
  configSchema?: Record<string, unknown>;
  stepDescription?: string;
  onConfigChange: (newConfig: Record<string, unknown>) => void;
  /** When true, the parent collapsible supplies the section heading. */
  embedded?: boolean;
}

export function StepConfigAssistant({
  stepType,
  config,
  configSchema,
  stepDescription,
  onConfigChange,
  embedded = false,
}: StepConfigAssistantProps) {
  const gt = useGT();
  const [instruction, setInstruction] = useState('');
  const [lastExplanation, setLastExplanation] = useState<string | null>(null);

  const { mutate, isPending, error } = useAssistStepConfig();

  const handleSubmit = useCallback(() => {
    const trimmed = instruction.trim();
    if (!trimmed || isPending) return;

    mutate(
      {
        stepType,
        currentConfig: config,
        configSchema,
        stepDescription,
        instruction: trimmed,
      },
      {
        onSuccess: (result) => {
          onConfigChange(result.config);
          setLastExplanation(result.explanation);
          setInstruction('');
        },
      },
    );
  }, [
    instruction,
    isPending,
    mutate,
    stepType,
    config,
    configSchema,
    stepDescription,
    onConfigChange,
  ]);

  return (
    <div className="space-y-2">
      {!embedded && (
        <div className="flex items-center gap-2">
          <div className="flex-1 h-px bg-border" />
          <span className="text-xs font-semibold uppercase tracking-wider text-muted-foreground flex items-center gap-1.5">
            <SparkleIcon className="h-3.5 w-3.5" />
            <T context="workflow step config AI assistant section label">
              AI Assistant
            </T>
          </span>
          <div className="flex-1 h-px bg-border" />
        </div>
      )}

      {lastExplanation && (
        <div className="rounded-md border border-primary/20 bg-primary/5 p-2">
          <p className="text-xs text-muted-foreground">{lastExplanation}</p>
        </div>
      )}

      {error && (
        <div className="rounded-md border border-destructive/20 bg-destructive/5 p-2">
          <p className="text-xs text-destructive">
            {error.message ||
              gt('Something went wrong', {
                context:
                  'workflow step config assistant fallback error message',
              })}
          </p>
        </div>
      )}

      <div className="flex min-w-0 items-stretch gap-2">
        <Textarea
          placeholder={gt("Describe how to change this step's config...", {
            context:
              'workflow step config AI assistant instruction input placeholder',
          })}
          value={instruction}
          onChange={(e) => setInstruction(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) {
              e.preventDefault();
              handleSubmit();
            }
          }}
          rows={2}
          className={cn(
            'min-h-0 min-w-0 flex-1 resize-none text-xs',
            isPending && 'opacity-50',
          )}
          disabled={isPending}
        />
        <Button
          onClick={handleSubmit}
          disabled={isPending || !instruction.trim()}
          className="h-auto shrink-0 self-stretch px-3"
        >
          {isPending ? (
            <span className="flex items-center gap-1.5">
              <SparkleIcon className="h-3.5 w-3.5 animate-spin" />
              <span className="text-xs">
                <T context="workflows">Thinking...</T>
              </span>
            </span>
          ) : (
            <span className="flex items-center gap-1.5">
              <ArrowRightIcon className="h-3.5 w-3.5" />
              <span className="text-xs">
                <T context="workflows">Apply</T>
              </span>
            </span>
          )}
        </Button>
      </div>
      <p className="text-[10px] text-muted-foreground">
        <T context="workflow step config AI assistant keyboard shortcut hint">
          Press Cmd+Enter to submit. The AI will modify the config based on your
          instruction.
        </T>
      </p>
    </div>
  );
}
